import React, { useState, useEffect, useRef, useCallback, useMemo, createContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";

import StarBorderOutlinedIcon from "@mui/icons-material/StarBorderOutlined";

import { ButtonUI, ConfirmPopup } from "../../../../Common";
import { FormTile } from "../../../../Common/Tile";
import NotAllowedDialog from "../../../../Common/NotAllowedDialog";
import MappingPopup from "./MappingPopup";
import CommonLayout from "../../../../Common/Layout";
import SourceForm from "./SourceForm";
import DestinationForm from "./DestinationForm";
import NextJobForm from "../../utils/NextJobForm";
import Scheduler from "../../utils/Scheduler";

import { showToast } from "../../../../../store/actions";
import { useCronReducer } from "../../../../../util/cron";
import WorkspaceService from "../../../../../service/workspace";
import { arrangeSchema, checkIsClusterExist, checkIsSchemaExist, setPrevState } from "../../../../../util/common";
import Snowflake from "../../../../../service/snowflake";
import { regexName } from "../../../../../util/regularExpress";
import {
  CONVERSION_FROM_SAS,
  DETAUL_CRON_VALUE,
  JOB_BY_TYPE,
  JOB_PURPOSES,
  JOB_PURPOSE_CONVERSION,
  JOB_PURPOSE_EXCHANGE,
} from "../../utils/JobData";
import { navScheduledJobs } from "../../../../kmk/AppData";
import HelpContextIcon from "../../../../Common/HelpPanel/HelpContextIcon";
import SQLService from "../../../../../service/mysqlService";
import Salesforce from "../../../../../service/salesforce";
import Veevavault from "../../../../../service/veevavault";
import DataPipelineService from "../../../../../service/dataPipelineService";
import SmallToolbar from "../../../../Common/SmallToolbar";
import CloneJob from "../../CloneJob";

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
    flexDirection: "column",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
  Toolbar: {
    fontSize: "1.1rem",
    backgroundColor: "white",
    color: "black",
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },
  MenuItem: {
    whiteSpace: "normal",
    wordWrap: "break-word",
    wordBreak: "break-all",
  },
}));

const DEFAULT_MAX_FILE_SIZE = 500;

export const DestinationContext = createContext();

export default function DatExchangeScheduler() {
  const { Workspace_id, Job_id } = useParams();

  const user = useSelector((state) => state.user.currentUser);
  const { defaultSourceValues, defaultCluster, salesforceDelimiters, delimiters, fileTypes, nullValues } = useSelector(
    (state) => state.config
  );

  const {
    compression: defaultCompression,
    type: defaultFileType,
    delimiter: defaultDelimiter,
    encoding: defaultEncoding,
    qualifier: defaultFieldEnclosedBy,
    errorAction: defaultErrorAction,
    nullValue: defaultNullValue,
  } = defaultSourceValues;
  const workspace = useSelector((state) => state.workspace);

  const { Name: workspaceName, IsDataVaultEnabled: isDataVaultEnabled, accessRole } = workspace.workspace;
  const currentUserFullName = useMemo(() => `${user.FirstName} ${user.LastName}`, [user.FirstName, user.LastName]);

  const [jobName, setJobName] = useState("");
  const [isEntryJob, setIsEntryJob] = useState(false);
  const [exchangeJobState, setExchangeJobState] = useState({
    loading: false,
    DataPipeline_id: "",
    source: "",
    query: "",
    queryError: "",
    locationList: [],
    sourceType: "",
    sourcePath: "",
    container: "",
    desinationContainer: "",
    sourcePathError: "",
    loadingDropBoxValues: false,
    destination: "",
    targetPath: "",
    filename: "",
    fileType: defaultFileType,
    customDelimeter: "",
    delimiter: defaultDelimiter,
    bucket: "",
    timeZone: "",
    scheduleEnabled: false,
    nextRunTime: "",
    hasHeaderRow: true,
    encoding: defaultEncoding,
    fieldEnclosedBy: defaultFieldEnclosedBy,
    fields: [],
    schemaName: "",
    groupBy: "",
    snowflakeSchemas: [],
    externalSnowflakeSchemas: [],
    tableList: [],
    tableName: "",
    destinationAction: "new",
    existingTableAction: "overwrite",
    tableNameError: "",
    mapping: false,
    isFieldsConfigured: false,
    allSchemas: [],
    showSchemaError: false,
    fieldsError: false,
    docuList: "",
    runJobAsUserId: user.User_id,
    runJobAsUserFullName: currentUserFullName,
    resetFiles: false,
    compression: defaultCompression,
    singleFile: false,
    maxFileSize: DEFAULT_MAX_FILE_SIZE,
    onError: defaultErrorAction,
    onErrorNumber: 0,
    skipFileError: "",
    nullValue: defaultNullValue,
    customNullValue: "",
    conversionFrom: CONVERSION_FROM_SAS,
    externalIdFieldName: "",
    renameFileRegex: false,
    unzipFiles: false,
    regexMatchPattern: "",
    regexReplacePattern: "",
  });

  const [runTimeState, setRunTimeState] = useState({
    cluster: "",
    clusterError: false,
    clusterList: [],
  });

  const [dataPipelineList, setDataPipelineList] = useState([]);
  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    loadingForConfirm: false,
  });
  const participantsExcludeVisitor = useRef([]);
  const [jobPurpose, setJobPurpose] = useState(JOB_PURPOSE_EXCHANGE);

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [notAllowedDialog, setNotAllowedDialog] = useState({
    confirmMessage: "",
    title: "",
    openConfirm: false,
  });

  const [restrictedObject, setRestrictedObject] = useState({
    schema: "",
    table: "",
  });

  const [openClone, setOpenClone] = useState(false);

  // Specifically for Quick import jobs
  const previousState = useRef({
    destination: "",
    delimiter: defaultDelimiter,
    hasHeaderRow: true,
    encoding: defaultEncoding,
    fieldEnclosedBy: defaultFieldEnclosedBy,
    fields: [],
    schemaName: "",
    tableName: "",
    destinationAction: "new",
    existingTableAction: "overwrite",
    source: "",
    sourcePath: "",
    nullValue: defaultNullValue,
  });

  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);
  const exchangeLoadedRef = useRef(false);
  const textRef = useRef(null);

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer("");

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const destinationType = useMemo(
    () =>
      exchangeJobState.locationList
        .find(({ Stage_id }) => Stage_id === exchangeJobState.destination)
        ?.Detail?.type?.toLowerCase() || "",
    [exchangeJobState.destination, exchangeJobState.locationList]
  );

  const isSourceExtSnowflake = useMemo(
    () => exchangeJobState?.sourceType?.toLowerCase() === "snowflake" && exchangeJobState.source !== "SNOWFLAKE",
    [exchangeJobState.source, exchangeJobState?.sourceType]
  );

  const isDestinationSnowflake = useMemo(() => destinationType?.toLowerCase() === "snowflake", [destinationType]);
  const isDestinationExtSnowflake = useMemo(
    () => isDestinationSnowflake && exchangeJobState.destination !== "SNOWFLAKE",
    [exchangeJobState.destination, isDestinationSnowflake]
  );

  const allowRename = useMemo(() => {
    const sourceType = exchangeJobState?.sourceType?.toLowerCase();
    if (sourceType?.toLowerCase() === "s3" && (destinationType === "s3" || destinationType === "sftp")) return true;
    if (destinationType === "s3" && (sourceType?.toLowerCase() === "sftp" || sourceType?.toLowerCase() === "azure"))
      return true;

    return false;
  }, [destinationType, exchangeJobState?.sourceType]);

  const handleCancel = () => {
    setPrevState(setDeleteJobState, { openConfirm: false });
  };

  const deleteExchangeJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteExchangeJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  // Check condition to disabled Save Button
  const disableSaveButton = () => {
    let disabledSave =
      jobName?.trim() &&
      jobPurpose &&
      exchangeJobState.locationList.length &&
      exchangeJobState.source &&
      exchangeJobState.destination;

    if (jobPurpose === JOB_PURPOSE_CONVERSION) disabledSave = disabledSave && exchangeJobState.conversionFrom;

    if (allowRename && exchangeJobState?.renameFileRegex)
      disabledSave =
        disabledSave && Boolean(exchangeJobState?.regexMatchPattern && exchangeJobState?.regexReplacePattern);

    if (["snowflake", "mysql"]?.includes(destinationType))
      disabledSave =
        disabledSave && !(exchangeJobState?.sourcePath && exchangeJobState?.sourcePath.split(",/").length > 1);

    if (["sftp", "azure", "s3", "sharepoint"].includes(exchangeJobState.sourceType.toLowerCase()))
      disabledSave = disabledSave && exchangeJobState?.sourcePath?.trim() && !exchangeJobState.sourcePathError;
    else if (["salesforce", "veevavault"].includes(destinationType))
      disabledSave = disabledSave && exchangeJobState?.query?.trim() && !exchangeJobState.queryError;
    else
      disabledSave =
        disabledSave &&
        exchangeJobState?.query?.trim() &&
        !exchangeJobState.queryError &&
        exchangeJobState?.filename?.trim() &&
        exchangeJobState.runJobAsUserId &&
        fileTypes.length &&
        exchangeJobState.fileType &&
        delimiters.length &&
        (exchangeJobState.delimiter === "custom" ? exchangeJobState.customDelimeter : exchangeJobState.delimiter) &&
        (exchangeJobState.nullValue === "custom" ? exchangeJobState.customNullValue : exchangeJobState.nullValue);

    if (isDestinationExtSnowflake || isSourceExtSnowflake)
      disabledSave =
        disabledSave &&
        exchangeJobState?.groupBy?.trim() &&
        exchangeJobState?.schemaName?.trim() &&
        runTimeState.cluster &&
        !runTimeState?.clusterError;

    if (!isDestinationSnowflake && !["mysql", "salesforce", "veevavault"].includes(destinationType))
      disabledSave = disabledSave && exchangeJobState?.targetPath?.trim();
    else {
      disabledSave =
        disabledSave &&
        exchangeJobState?.schemaName?.trim() &&
        !exchangeJobState.tableNameError &&
        exchangeJobState.tableName.trim() &&
        fileTypes.length &&
        exchangeJobState.fileType &&
        exchangeJobState.onError &&
        !exchangeJobState.skipFileError &&
        (exchangeJobState.nullValue === "custom" ? exchangeJobState.customNullValue : exchangeJobState.nullValue) &&
        (exchangeJobState.fileType === "csv" && exchangeJobState.sourceType.toLowerCase() === "snowflake"
          ? exchangeJobState.runJobAsUserId
          : true) &&
        (exchangeJobState.fileType === "csv"
          ? delimiters.length &&
            (exchangeJobState.delimiter === "custom" ? exchangeJobState.customDelimeter : exchangeJobState.delimiter)
          : true) &&
        (exchangeJobState.destination === "SNOWFLAKE" || exchangeJobState.source === "SNOWFLAKE"
          ? exchangeJobState?.runJobAsUserId
          : true) &&
        (destinationType !== "salesforce" ||
          (exchangeJobState?.schemaName !== "update" && exchangeJobState?.schemaName !== "upsert") ||
          exchangeJobState.externalIdFieldName) &&
        (destinationType !== "veevavault" ||
          !["update", "upsert", "delete"].includes(exchangeJobState?.schemaName) ||
          exchangeJobState.externalIdFieldName);
    }
    return !disabledSave;
  };

  const handleSave = async (e, clone = false) => {
    if (["snowflake", "mysql"]?.includes(destinationType?.toLowerCase()) && !exchangeJobState.isFieldsConfigured) {
      const {
        destination,
        delimiter,
        hasHeaderRow,
        encoding,
        fieldEnclosedBy,
        schemaName,
        tableName,
        destinationAction,
        existingTableAction,
        source,
      } = previousState.current;
      if (
        destination !== exchangeJobState.destination ||
        !(delimiter === exchangeJobState.delimiter || delimiter === exchangeJobState.customDelimeter) ||
        hasHeaderRow !== exchangeJobState.hasHeaderRow ||
        encoding !== exchangeJobState.encoding ||
        fieldEnclosedBy !== exchangeJobState.fieldEnclosedBy ||
        schemaName !== exchangeJobState.schemaName ||
        tableName !== exchangeJobState.tableName ||
        destinationAction !== exchangeJobState.destinationAction ||
        existingTableAction !== exchangeJobState.existingTableAction ||
        source !== exchangeJobState.source
      ) {
        setPrevState(setNotAllowedDialog, {
          openConfirm: true,
          confirmMessage: (
            <span className={classes.deleteReffConfirm}>
              <span>
                {Job_id
                  ? "Source or destination file properties were updated, please save the new table configurations to update the job."
                  : "Please configure the table to save the job."}
              </span>
            </span>
          ),
          title: "Action blocked",
        });
        return;
      }
    }
    setPrevState(setExchangeJobState, { loading: true });

    let response = null;
    const submitData = {
      name: jobName.trim(),
      jobPurpose,
      DataPipeline_id: exchangeJobState.DataPipeline_id,
      source_stage_id: exchangeJobState.source,
      dest_stage_id: exchangeJobState.destination,
      ...(isDestinationExtSnowflake || isSourceExtSnowflake
        ? { database: exchangeJobState.groupBy || "", Cluster: runTimeState.cluster || "" }
        : {}),
      location: ["mysql", "salesforce", "snowflake", "veevavault"].includes(destinationType)
        ? exchangeJobState.source
        : exchangeJobState.targetPath,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: exchangeJobState.scheduleEnabled || false,
      files: exchangeJobState.sourcePath?.split(",/").map((item, key) => (key !== 0 ? "/" + item : item)) || [],
      query: exchangeJobState.query,
      filename: exchangeJobState.filename,
      filetype: exchangeJobState.fileType,
      ...(exchangeJobState.fileType === "csv"
        ? {
            delimiter:
              exchangeJobState.delimiter === "custom" ? exchangeJobState.customDelimeter : exchangeJobState.delimiter,
            fieldEnclosedBy: exchangeJobState.fieldEnclosedBy,
            encoding: exchangeJobState.encoding,
            hasHeaderRow: exchangeJobState.hasHeaderRow,
          }
        : {
            delimiter: defaultDelimiter,
            fieldEnclosedBy: defaultFieldEnclosedBy,
            encoding: defaultEncoding,
            hasHeaderRow: true,
          }),
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      Workspace_id,
      cluster: defaultCluster,
      schemaName: exchangeJobState.schemaName,
      tableName: exchangeJobState.tableName,
      append: exchangeJobState.destinationAction === "existing" && exchangeJobState.existingTableAction === "append",
      fields: exchangeJobState.fields,
      bucket: exchangeJobState.bucket,
      path: exchangeJobState.sourcePath,
      runJobAsUserId: exchangeJobState.runJobAsUserId,
      runJobAsUserFullName: exchangeJobState.runJobAsUserFullName,
      resetFiles: exchangeJobState?.resetFiles || false,
      compression: exchangeJobState.compression,
      nullValue:
        (exchangeJobState.nullValue === "custom" ? exchangeJobState.customNullValue : exchangeJobState.nullValue) ||
        defaultNullValue,
      onError: exchangeJobState.onError,
      onErrorNumber: exchangeJobState.onErrorNumber,
      singleFile: exchangeJobState.singleFile,
      maxFileSize: parseInt(exchangeJobState.maxFileSize),
      conversionFrom: exchangeJobState.conversionFrom,
      externalIdFieldName: exchangeJobState.externalIdFieldName,
      isEntryJob,
      renameFileRegex: exchangeJobState.renameFileRegex,
      unzipFiles: exchangeJobState.unzipFiles,
      ...(exchangeJobState.renameFileRegex
        ? {
            regexMatchPattern: exchangeJobState.regexMatchPattern,
            regexReplacePattern: exchangeJobState.regexReplacePattern,
          }
        : {
            regexMatchPattern: "",
            regexReplacePattern: "",
          }),
    };

    const wsService = new WorkspaceService();
    if (!Job_id) response = await wsService.createExchangeJob(submitData, Workspace_id);
    else response = await wsService.updateExchangeJob(submitData, Workspace_id, Job_id);

    if (response.success) {
      if (clone) {
        setPrevState(setExchangeJobState, { loading: false });
        exchangeLoadedRef.current = false;
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setExchangeJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const loadSnowflakeTables = useCallback(
    async (schema) => {
      if (!schema) return [];
      const snowflake = new Snowflake();
      const response = await snowflake.getTables(Workspace_id, schema.toUpperCase());
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      if (response.success) return response.data;
      return [];
    },
    [Workspace_id, navigate]
  );

  const loadExternalSnowflakeTables = useCallback(
    async (location, database, schema) => {
      if (!database || !schema || !location) return [];
      const wsService = new WorkspaceService();
      const response = await wsService.getExternalSnowflakeTables(Workspace_id, location, { database, schema });
      if (response.success) return response.data;
      return [];
    },
    [Workspace_id]
  );

  const loadmysqlTables = useCallback(
    async (Stage_id, schemaName) => {
      if (!Stage_id || !schemaName) return [];
      const mysqlService = new SQLService();
      const response = await mysqlService.getMysqlTables(Workspace_id, Stage_id, { schemaName });
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      if (response.success) return response.data;
      return [];
    },
    [Workspace_id, navigate]
  );

  const loadSalesforceObjects = useCallback(
    async (Stage_id) => {
      if (!Stage_id) return [];
      const salesforce = new Salesforce();
      const response = await salesforce.getObjects(Workspace_id, Stage_id);

      if (response.success)
        return {
          tableList: response.data.objects.map(({ value }) => value),
          ...(Job_id ? {} : { schemaName: "update" }),
        };
      return { tableList: [], ...(Job_id ? {} : { schemaName: "update" }) };
    },
    [Workspace_id, Job_id]
  );

  const loadVeevaObjects = useCallback(
    async (Stage_id) => {
      if (!Stage_id) return [];
      const veevavault = new Veevavault();
      const response = await veevavault.getVeevaObjects(Workspace_id, Stage_id);

      if (response.success)
        return {
          tableList: response.data.objects.map(({ value }) => value),
          ...(Job_id ? {} : { schemaName: "update" }),
        };
      return { tableList: [], ...(Job_id ? {} : { schemaName: "update" }) };
    },
    [Workspace_id, Job_id]
  );

  const handleUserChange = useCallback((e, { runJobAsUserId, runJobAsUserFullName }) => {
    setPrevState(setExchangeJobState, {
      runJobAsUserId,
      runJobAsUserFullName,
    });
  }, []);

  const handleRunUnderMyName = useCallback(
    (e) => handleUserChange(e, { runJobAsUserId: user.User_id, runJobAsUserFullName: currentUserFullName }),
    [currentUserFullName, handleUserChange, user.User_id]
  );

  const loadmysqlSchemas = useCallback(
    async (Stage_id) => {
      const mysqlService = new SQLService();
      const response = await mysqlService.getMysqlSchemas(Workspace_id, Stage_id);
      if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
      let allSchemas = [];
      if (response.success) allSchemas = response.data;
      return allSchemas;
    },
    [Workspace_id, navigate]
  );

  useEffect(() => {
    if (![1, 2].includes(accessRole)) {
      dispatch(
        showToast({
          message: "You do not have permission to perform this action!",
          type: "error",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
  }, [Workspace_id, accessRole, dispatch, navigate]);

  const loadSnowflakeSchemas = useCallback(async () => {
    const wsService = new WorkspaceService();
    const response = await wsService.getJobsSchemas(Workspace_id);
    if (response === "Unauthorized") navigate(`/my-workspaces/${Workspace_id}`);
    const returnData = { snowflakeSchemas: [], allSchemas: [] };

    if (response.success) {
      returnData.snowflakeSchemas = response.data.schemasWithReadAndWrite;
      returnData.allSchemas = response.data.allSchemas;
    }
    return returnData;
  }, [Workspace_id, navigate]);

  const loadExternalSnowflakeSchemas = useCallback(
    async (location = false, orignalResponse = false) => {
      let returnData = { externalSnowflakeSchemas: [] };
      if (!location) return returnData;
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeSchemas(Workspace_id, location, {
        schemasOnly: true,
      });
      if (success) {
        const externalSnowflakeSchemas = arrangeSchema(data);
        setPrevState(setExchangeJobState, { externalSnowflakeSchemas });
        returnData = { externalSnowflakeSchemas, ...(orignalResponse ? { data } : {}) };
      }
      return returnData;
    },
    [Workspace_id]
  );

  const getExternalSnowflakeCluster = useCallback(
    async (location) => {
      if (!location || location === "SNOWFLAKE") return [];
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getExternalSnowflakeCluster(Workspace_id, location);
      if (success && data?.length) {
        const clusterList = data?.map((cluster) => ({ value: cluster?.name || "" }));
        return clusterList;
      }
      return [];
    },
    [Workspace_id]
  );

  const loadSchemasClusters = useCallback(
    async (locationType, location, fromSource = false) => {
      if (fromSource && locationType !== "snowflake") return;
      else if (
        ["", "s3", "sftp", "azure"].includes(locationType) ||
        (["mysql", "salesforce", "veevavault"].includes(locationType) && !location)
      )
        return;

      setPrevState(setExchangeJobState, { loading: true });
      let data = {};
      if (locationType === "mysql") data.allSchemas = (await loadmysqlSchemas(location)) || [];
      else if (locationType === "snowflake") {
        if (location === "SNOWFLAKE") data = await loadSnowflakeSchemas();
        else {
          const [schemaData, clusterList] = await Promise.all([
            loadExternalSnowflakeSchemas(location),
            getExternalSnowflakeCluster(location),
          ]);

          data = schemaData;
          // Check if the cluster exists (do not display error as connection changed)
          const isClusterExist = checkIsClusterExist(clusterList, runTimeState?.cluster);
          setRunTimeState({ cluster: isClusterExist ? runTimeState?.cluster : "", clusterError: false, clusterList });
        }
      } else if (locationType === "salesforce")
        data = await loadSalesforceObjects(location); //here "update" is default selection for operation type
      else if (locationType === "veevavault") data = await loadVeevaObjects(location); //here "update" is default selection for operation type

      setPrevState(setExchangeJobState, {
        ...data,
        loading: false,
        loadingDropBoxValues: false,
      });
    },
    [
      getExternalSnowflakeCluster,
      loadExternalSnowflakeSchemas,
      loadSalesforceObjects,
      loadSnowflakeSchemas,
      loadVeevaObjects,
      loadmysqlSchemas,
      runTimeState?.cluster,
    ]
  );

  useEffect(() => {
    if (!exchangeJobState.sourceType || !destinationType || !exchangeJobState.delimiter) return;

    const salesforceDelims =
      ["salesforce", "veevavault"].includes(destinationType) ||
      exchangeJobState.sourceType.toLowerCase() === "salesforce";
    const _delimiters = salesforceDelims ? salesforceDelimiters : delimiters;

    if (!_delimiters.some(({ value }) => value === exchangeJobState.delimiter)) {
      setExchangeJobState((prev) => ({
        ...prev,
        delimiter: salesforceDelims ? _delimiters[0].value : defaultDelimiter,
      }));
    }
  }, [
    exchangeJobState.delimiter,
    destinationType,
    salesforceDelimiters,
    delimiters,
    defaultDelimiter,
    exchangeJobState.sourceType,
  ]);

  useEffect(() => {
    const getDataExchangeSchedule = async () => {
      const wsService = new WorkspaceService();

      const response = await wsService.getDataExchangeSchedule(Workspace_id, Job_id);
      if (response === "Unauthorized") return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

      if (response.success) {
        setPrevState(setNextJobState, {
          nextDataPipelineId: response.data.NextPipeline_id || "",
          nextJobId: response.data.NextJob_id || nextJobIdRef.current,
          errorDataPipelineId: response.data.ErrorPipeline_id || "",
          errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
          nextJobToggle: response.data.enableNextJob || false,
        });

        setJobPurpose(response.data.Detail.jobPurpose || JOB_PURPOSE_EXCHANGE);

        const destinationType = response.data.Detail?.destinationType?.toLowerCase() || "snowflake";
        const sourceType = response.data.Detail?.sourceType?.toLowerCase() || "snowflake";

        dispatchValues({
          type: "set_values",
          value: response.data.Schedule,
        });

        if (response.data.status === "draft") {
          setJobName(response.data.Name);
          return {
            timeZone: response.data.ServerTimeZone,
            scheduleEnabled: response.data.Enabled,
            nextRunTime: response.data.nextRun,
            DataPipeline_id: response.data.DataPipeline_id,
          };
        } else {
          const sourcePath = ["mysql", "salesforce", "snowflake", "veevavault"].includes(destinationType)
            ? response.data.Detail.path
            : response.data.Detail.files?.join(",");
          const destinationPath = response.data.Detail.location;

          const _delimiters =
            ["salesforce", "veevavault"].includes(destinationType) || sourceType === "salesforce"
              ? salesforceDelimiters
              : delimiters;

          /* Added logic for custom option selection in Delimiters dropdown */
          const formatDelimiter = { delimiter: response.data.Detail.delimiter, customDelimeter: "" };
          if (formatDelimiter.delimiter && !_delimiters.some((obj) => obj.value === response.data.Detail.delimiter)) {
            formatDelimiter.delimiter = "custom";
            formatDelimiter.customDelimeter = response.data.Detail.delimiter;
          }

          const formatNullValue = { nullValue: response.data.Detail.nullValue, customNullValue: "" };
          if (formatNullValue.nullValue && !nullValues.some((obj) => obj.value === response.data.Detail.nullValue)) {
            formatNullValue.nullValue = "custom";
            formatNullValue.customNullValue = response.data.Detail.nullValue;
          }

          const isQuickImportJob =
            (["s3", "azure", "sftp", "sharepoint"].includes(sourceType) &&
              destinationType?.toLowerCase() === "snowflake") ||
            (sourceType === "snowflake" && destinationType === "s3") ||
            ["mysql", "salesforce", "snowflake", "veevavault"].includes(destinationType);

          let allSchemas = [];
          let snowflakeSchemas = [];
          let externalSnowflakeSchemas = [];
          let schemasByDB = {};
          let tableList = [];
          let isSchemaExist = true;
          let haveSchemaPermission = true;
          let isTableCreated = true;

          // This entire If block focuses on what if schema/table is deleted or it is removed from access
          if (isQuickImportJob) {
            if (destinationType === "mysql")
              allSchemas = (await loadmysqlSchemas(response.data.Detail.dest_stage_id)) || [];
            else if (response.data.Detail.dest_stage_id === "SNOWFLAKE") {
              const snowflakeResponse = await loadSnowflakeSchemas();
              snowflakeSchemas = snowflakeResponse?.snowflakeSchemas;
              allSchemas = snowflakeResponse?.allSchemas;
            } else if (
              (sourceType === "snowflake" && response.data?.Detail?.source_stage_id !== "SNOWFLAKE") ||
              (destinationType === "snowflake" && response.data?.Detail?.dest_stage_id !== "SNOWFLAKE")
            ) {
              const [schemaData, clusterList] = await Promise.all([
                loadExternalSnowflakeSchemas(
                  sourceType === "snowflake"
                    ? response.data.Detail.source_stage_id
                    : response.data.Detail.dest_stage_id,
                  true
                ),
                getExternalSnowflakeCluster(
                  sourceType === "snowflake" ? response.data.Detail.source_stage_id : response.data.Detail.dest_stage_id
                ),
              ]);

              // Destructure the result from schemaData
              const { externalSnowflakeSchemas: schemas, data } = schemaData;

              // Update state with the fetched data
              externalSnowflakeSchemas = [...schemas];
              schemasByDB = data || {};

              // Check if the cluster exists
              const isClusterExist = checkIsClusterExist(clusterList, response.data?.Detail?.Cluster);

              // Update runtime state
              setRunTimeState({
                cluster: response.data?.Detail?.Cluster || "",
                clusterError: !isClusterExist,
                clusterList,
              });
            }

            if ([sourceType, destinationType]?.includes("snowflake") || destinationType === "mysql") {
              if (
                response.data.Detail.dest_stage_id === "SNOWFLAKE" ||
                response.data.Detail.source_stage_id === "SNOWFLAKE" ||
                destinationType === "mysql"
              )
                isSchemaExist = allSchemas.some(
                  (schema) => schema.toLowerCase() === response?.data?.Detail?.schemaName?.toLowerCase()
                );
              else
                isSchemaExist = checkIsSchemaExist(
                  schemasByDB,
                  response.data?.Detail?.database,
                  response.data?.Detail?.schemaName
                );
            } else isSchemaExist = false;

            if (isSchemaExist || ["salesforce", "veevavault"].includes(destinationType)) {
              if (response.data.Detail.dest_stage_id === "SNOWFLAKE") {
                haveSchemaPermission = snowflakeSchemas.some(
                  (schema) => schema.toLowerCase() === response.data.Detail.schemaName.toLowerCase()
                );
                if (!haveSchemaPermission) {
                  setRestrictedObject({
                    schema: response.data.Detail.schemaName,
                    table: response.data.Detail.tableName,
                  });
                }
                tableList = await loadSnowflakeTables(response.data.Detail.schemaName);
                isTableCreated = tableList.some(
                  (table) => table.toLowerCase() === response.data.Detail.tableName.toLowerCase()
                );
              } else if (destinationType === "snowflake") {
                tableList = await loadExternalSnowflakeTables(
                  response.data.Detail.dest_stage_id,
                  response.data.Detail.database,
                  response.data?.Detail?.schemaName
                );
                isTableCreated = tableList.some(
                  (table) => table.toLowerCase() === response.data?.Detail?.tableName?.toLowerCase()
                );
              } else if (destinationType === "salesforce") {
                isSchemaExist = true;
                tableList = (await loadSalesforceObjects(response.data.Detail.dest_stage_id)).tableList;
                isTableCreated = tableList.some(
                  (table) => table.toLowerCase() === response.data.Detail.tableName.toLowerCase()
                );
              } else if (destinationType === "mysql") {
                tableList = await loadmysqlTables(response.data.Detail.dest_stage_id, response.data.Detail.schemaName);
                isTableCreated = tableList.some(
                  (table) => table.toLowerCase() === response.data.Detail.tableName.toLowerCase()
                );
              } else if (destinationType === "veevavault") {
                isSchemaExist = true;
                tableList = (await loadVeevaObjects(response.data.Detail.dest_stage_id)).tableList;
                isTableCreated = tableList.some(
                  (table) => table.toLowerCase() === response.data.Detail.tableName.toLowerCase()
                );
              }
            }
          }

          // Flag for rename file
          const renameFileRegex = Boolean(
            response?.data?.Detail?.regexMatchPattern && response?.data?.Detail?.regexReplacePattern
          );

          if (isQuickImportJob) {
            previousState.current = {
              destination: response.data.Detail.dest_stage_id,
              source: response.data.Detail.source_stage_id,
              sourcePath: sourcePath && sourcePath[0] !== "/" ? "/" + sourcePath : sourcePath,
              fields: response.data.Detail.fields,
              schemaName: isSchemaExist ? response.data.Detail.schemaName : "",
              groupBy: isSchemaExist ? response.data.Detail.database : "",
              tableName: isSchemaExist ? response.data.Detail.tableName : "",
              destinationAction: isSchemaExist && isTableCreated ? "existing" : "new",
              existingTableAction: isSchemaExist && response.data.Detail.append ? "append" : "overwrite",
              nullValue: formatNullValue?.nullValue || defaultNullValue,
              renameFileRegex,
              unzipFiles: response.data.Detail.unzipFiles || false,
              regexMatchPattern: response?.data?.Detail?.regexMatchPattern || "",
              regexReplacePattern: response?.data?.Detail?.regexReplacePattern || "",
              ...(response.data.Detail.filetype === "csv"
                ? {
                    delimiter: response.data.Detail.delimiter || defaultDelimiter,
                    hasHeaderRow: response.data.Detail.hasHeaderRow ?? true,
                    encoding: response.data.Detail.encoding || defaultEncoding,
                    fieldEnclosedBy: response.data.Detail.fieldEnclosedBy || defaultFieldEnclosedBy,
                  }
                : {
                    delimiter: defaultDelimiter,
                    fieldEnclosedBy: defaultFieldEnclosedBy,
                    encoding: defaultEncoding,
                    hasHeaderRow: true,
                  }),
              ...(sourceType === "snowflake" || ["mysql", "salesforce", "veevavault"].includes(sourceType)
                ? { fieldEnclosedBy: response.data.Detail.fieldEnclosedBy || defaultFieldEnclosedBy }
                : {}),
            };
          }

          // =====================================To prevent rerendering ==============================================
          exchangeLoadedRef.current = true;
          // ==========================================================================================================

          setJobName(response.data.Name);

          let runJobAsUserId = null;
          let runJobAsUserFullName = "";

          if (response.data.Detail?.runJobAsUserId) {
            const runJobAsUserIdExists = participantsExcludeVisitor.current.some(
              (user) => user.User_id === response.data.Detail?.runJobAsUserId
            );

            if (runJobAsUserIdExists) {
              runJobAsUserId = response.data.Detail?.runJobAsUserId;
              runJobAsUserFullName = response.data.Detail?.runJobAsUserFullName;
            }
          } else {
            runJobAsUserId = user.User_id;
            runJobAsUserFullName = currentUserFullName;
          }

          return {
            source: response.data.Detail.source_stage_id,
            DataPipeline_id: response.data.DataPipeline_id,
            sourcePath: sourcePath && sourcePath[0] !== "/" ? "/" + sourcePath : sourcePath,
            resetFiles: response.data.Detail?.resetFiles ? response.data.Detail.resetFiles : false,
            query: response.data.Detail.query,
            filename: response.data.Detail.filename,
            fileType: response.data.Detail?.filetype || defaultFileType,
            customDelimeter: formatDelimiter.customDelimeter,
            delimiter: formatDelimiter?.delimiter || defaultDelimiter,
            customNullValue: formatNullValue.customNullValue,
            nullValue: formatNullValue?.nullValue || defaultNullValue,
            destination: response.data.Detail.dest_stage_id,
            allSchemas,
            snowflakeSchemas,
            externalSnowflakeSchemas,
            targetPath: destinationPath && destinationPath[0] !== "/" ? "/" + destinationPath : destinationPath,
            timeZone: response.data.ServerTimeZone,
            scheduleEnabled: response.data.Enabled,
            nextRunTime: response.data.nextRun,
            sourceType: response.data.Detail.sourceType,
            bucket: response.data.Detail?.bucket,
            docuList: response.data.Detail?.docuList,
            runJobAsUserId,
            runJobAsUserFullName,
            hasHeaderRow: response.data.Detail.hasHeaderRow === undefined ? true : response.data.Detail.hasHeaderRow,
            compression: response.data.Detail.compression || defaultCompression,
            onError: response.data.Detail.onError || defaultErrorAction,
            onErrorNumber: response.data.Detail.onErrorNumber || 0,
            singleFile: response.data.Detail.singleFile || false,
            maxFileSize: parseInt(response.data.Detail.maxFileSize || DEFAULT_MAX_FILE_SIZE),
            externalIdFieldName: response.data.Detail.externalIdFieldName || "",
            renameFileRegex,
            unzipFiles: response.data.Detail.unzipFiles || false,
            regexMatchPattern: response?.data?.Detail?.regexMatchPattern || "",
            regexReplacePattern: response?.data?.Detail?.regexReplacePattern || "",
            ...(response?.data?.Detail?.jobPurpose === JOB_PURPOSE_CONVERSION
              ? {
                  conversionFrom: response.data.Detail.conversionFrom || CONVERSION_FROM_SAS,
                }
              : {}),
            ...(isQuickImportJob
              ? {
                  encoding: response.data.Detail.encoding || defaultEncoding,
                  fields: response.data.Detail.fields,
                  schemaName: isSchemaExist ? response.data.Detail.schemaName : "",
                  groupBy: isSchemaExist ? response.data.Detail.database : "",
                  tableName: isSchemaExist ? response.data.Detail.tableName : "",
                  tableList,
                  showSchemaError: !isSchemaExist,
                  destinationAction: isSchemaExist && isTableCreated ? "existing" : "new",
                  existingTableAction: isSchemaExist && response.data.Detail.append ? "append" : "overwrite",
                }
              : {}),
            ...(isQuickImportJob ||
            sourceType === "snowflake" ||
            ["mysql", "salesforce", "veevavault"].includes(sourceType)
              ? { fieldEnclosedBy: response.data.Detail.fieldEnclosedBy || defaultFieldEnclosedBy }
              : {}),
          };
        }
      } else return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    };

    const getTimezone = async () => {
      const wsService = new WorkspaceService();
      const result = await wsService.getTimezone(Workspace_id);

      return result.data;
    };

    //Get Location List for "Connection" Dropdown
    const getLocationList = async () => {
      const wsService = new WorkspaceService();
      const locations = await wsService.getLocationList(Workspace_id);

      const snowflakeLocation = {
        Stage_id: "SNOWFLAKE",
        Name: workspaceName + " (Current Workspace DB)",
        Detail: {
          type: "snowflake",
        },
      };

      return {
        locationList: [snowflakeLocation, ...locations],
      };
    };

    const getDataPipelineList = async () => {
      const pipeline = new DataPipelineService();
      const response = await pipeline.getDataPipelineList(Workspace_id);
      if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
      return [];
    };

    const getWorkspaceParticipants = async () => {
      const wsService = new WorkspaceService();
      const { success, data } = await wsService.getWorkspaceParticipants(Workspace_id, [1, 2]);
      if (success) participantsExcludeVisitor.current = data;
    };

    (async () => {
      if (exchangeLoadedRef.current === true) return true;

      setPrevState(setExchangeJobState, { loading: true, loadingDropBoxValues: true });

      const [locationList, jobOrTimeZone, , dataPipelines] = await Promise.all([
        getLocationList(),
        Job_id ? getDataExchangeSchedule() : getTimezone(),
        getWorkspaceParticipants(),
        getDataPipelineList(),
      ]);

      let initialState = {
        ...locationList,
        ...(Job_id ? { ...jobOrTimeZone } : { timeZone: jobOrTimeZone }),
      };

      if (!Job_id) {
        initialState.runJobAsUserId = user.User_id;
        initialState.runJobAsUserFullName = currentUserFullName;
      }

      if (!initialState.DataPipeline_id)
        initialState.DataPipeline_id = dataPipelines?.find((pipeline) => pipeline.IsDefault === 1)?.DataPipeline_id;
      setDataPipelineList(dataPipelines);

      if (initialState.sourceType && initialState.sourceType.toLowerCase() === "azure")
        initialState.container =
          initialState.locationList.find((loc) => loc.Stage_id === initialState.source)?.Detail.container || "";

      const desinationConnection = initialState.locationList.find((loc) => loc.Stage_id === initialState.destination);

      if (desinationConnection) initialState.desinationContainer = desinationConnection?.Detail?.container || "";

      if (Job_id) {
        const currentPipeline = dataPipelines.find(
          ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(initialState.DataPipeline_id)
        );
        if (currentPipeline.EntryJob_id === parseInt(Job_id)) setIsEntryJob(true);
      }

      setPrevState(setExchangeJobState, {
        ...initialState,
        loading: false,
        loadingDropBoxValues: false,
      });
    })();
  }, [
    Job_id,
    Workspace_id,
    currentUserFullName,
    defaultCompression,
    defaultDelimiter,
    defaultEncoding,
    defaultErrorAction,
    defaultFieldEnclosedBy,
    defaultFileType,
    defaultNullValue,
    delimiters,
    dispatchValues,
    getExternalSnowflakeCluster,
    loadExternalSnowflakeSchemas,
    loadExternalSnowflakeTables,
    loadSalesforceObjects,
    loadVeevaObjects,
    loadSnowflakeSchemas,
    loadSnowflakeTables,
    loadmysqlSchemas,
    loadmysqlTables,
    navigate,
    nullValues,
    salesforceDelimiters,
    user.User_id,
    workspaceName,
  ]);

  // If connection is sharepoint is sharepoint and fileType is parquet then changing it to csv
  useEffect(() => {
    if (exchangeJobState.sourceType === "sharepoint" && exchangeJobState.fileType === "parquet")
      setPrevState(setExchangeJobState, { fileType: "csv" });
  }, [exchangeJobState.fileType, exchangeJobState.sourceType]);

  /**
   * Alpha Numeric Characters only with Space allow (first letter should Alpha)
   * @param {*} str
   * @returns
   */
  const handleJobName = useCallback((e) => {
    const str = e.target.value;
    const isValid = regexName(str, "jobName", "_ -");
    if (isValid) setJobName(str);
  }, []);

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback(
    (e) => {
      setPrevState(setExchangeJobState, { DataPipeline_id: e.target.value });
      if (exchangeJobState.DataPipeline_id) setIsEntryJob(false);
      resetNextJob();
    },
    [exchangeJobState.DataPipeline_id]
  );

  const handleJobPurposeChange = useCallback(
    (e) => {
      setJobPurpose(e.target.value);

      setExchangeJobState((prev) => {
        return {
          ...prev,
          destination: "",
          delimiter: defaultDelimiter,
          hasHeaderRow: true,
          encoding: defaultEncoding,
          fieldEnclosedBy: defaultFieldEnclosedBy,
          fields: [],
          schemaName: "",
          tableName: "",
          destinationAction: "new",
          existingTableAction: "overwrite",
          source: "",
          sourcePath: "",
          sourceType: "",
          container: "",
          desinationContainer: "",
          nullValue: defaultNullValue,
          customDelimeter: "",
          conversionFrom: CONVERSION_FROM_SAS,
          fileType: defaultFileType,
          compression: defaultCompression,
          onError: defaultErrorAction,
          externalIdFieldName: "",
          renameFileRegex: false,
          unzipFiles: false,
          regexMatchPattern: "",
          regexReplacePattern: "",
        };
      });
    },
    [
      defaultCompression,
      defaultFileType,
      defaultDelimiter,
      defaultEncoding,
      defaultFieldEnclosedBy,
      defaultErrorAction,
      defaultNullValue,
    ]
  );

  // Form component to display Add Job Name Field Section
  const addJobForm = useCallback(() => {
    return (
      <Grid container direction={"row"} spacing={2}>
        <Grid>
          <TextField
            value={jobName}
            sx={{ width: { xs: "100%", sm: "300px" } }}
            required
            label="Job Name"
            inputProps={{ maxLength: 30 }}
            onChange={handleJobName}
          />
        </Grid>
        <Grid>
          {isDataVaultEnabled && (
            <FormControl sx={{ width: { xs: "100%", sm: "300px" } }} required>
              <InputLabel>Pipeline</InputLabel>
              <Select
                value={exchangeJobState.DataPipeline_id || ""}
                onChange={confirmPipelineChange}
                label="Pipeline"
                disabled={!!Job_id}
                MenuProps={{ PaperProps: { style: { maxHeight: "300px", maxWidth: "300px" } } }}
                renderValue={(selected) => {
                  const selectedPipeline = dataPipelineList.find((p) => p.DataPipeline_id === selected);
                  const isOverflow = (textRef.current?.scrollWidth > textRef.current?.clientWidth || false) && !!Job_id;

                  return (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Tooltip
                        placement="bottom"
                        title={selectedPipeline?.Name}
                        arrow
                        disableHoverListener={!isOverflow}
                      >
                        <Typography
                          variant="body1"
                          ref={textRef}
                          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "200px" }}
                        >
                          {selectedPipeline?.Name || "Select a pipeline"}
                        </Typography>
                      </Tooltip>
                      {selectedPipeline?.IsDefault === 1 && (
                        <Tooltip placement="right" title="Default Pipeline" arrow>
                          <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                        </Tooltip>
                      )}
                    </Stack>
                  );
                }}
              >
                {dataPipelineList.map((p) => (
                  <MenuItem key={p.DataPipeline_id} value={p.DataPipeline_id} className={classes.MenuItem}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body1" sx={{ m: 0, p: 0 }}>
                        {p.Name}
                      </Typography>
                      {p.IsDefault === 1 && (
                        <Tooltip placement="right" title="Default Pipeline" arrow>
                          <StarBorderOutlinedIcon sx={{ fontSize: "22px" }} color="info" />
                        </Tooltip>
                      )}
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>
        <Grid>
          <TextField
            select
            required
            fullWidth
            variant="outlined"
            sx={{ width: { xs: "100%", sm: "300px" } }}
            label="Operation"
            value={jobPurpose || JOB_PURPOSE_EXCHANGE}
            onChange={handleJobPurposeChange}
          >
            {JOB_PURPOSES.map(({ Id, Label }) => (
              <MenuItem key={Id} value={Id}>
                {Label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox />}
              value={isEntryJob}
              checked={isEntryJob}
              onChange={() => setIsEntryJob((prev) => !prev)}
              label="Set as the entry point for the pipeline"
            />
          </FormGroup>
        </Grid>
      </Grid>
    );
  }, [
    jobName,
    handleJobName,
    isDataVaultEnabled,
    exchangeJobState.DataPipeline_id,
    dataPipelineList,
    jobPurpose,
    handleJobPurposeChange,
    isEntryJob,
    confirmPipelineChange,
    Job_id,
    classes.MenuItem,
  ]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    {
      link: `/my-workspaces/${Workspace_id}`,
      displayName: workspaceName,
      active: false,
    },
    {
      link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`,
      displayName: "Data Pipelines",
      active: false,
    },
    { link: "#", displayName: `${Job_id ? "Edit Data Exchange Job" : "Add Data Exchange Job"}`, active: true },
  ];

  const updatePreviousRef = (newState) => {
    previousState.current = newState;
  };

  const toggleSchedule = useCallback(
    () => setExchangeJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  const destContextValue = useMemo(
    () => ({
      restrictedObject,
      loadSnowflakeTables,
      loadExternalSnowflakeTables,
      loadmysqlTables,
    }),
    [restrictedObject, loadSnowflakeTables, loadExternalSnowflakeTables, loadmysqlTables]
  );

  return (
    <CommonLayout title="Job Scheduler" loading={exchangeJobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          overflowX: "hidden",
        }}
        id="main_box"
      >
        <Backdrop
          sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={Boolean(exchangeJobState.loading || nextJobState.loading)}
        >
          <CircularProgress color="primary" />
        </Backdrop>

        <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
          <Box className={classes.subContainer}>
            <Box className={classes.PanelContainer}>
              <Grid container direction="row" sx={{ mb: 4 }}>
                <Grid sx={{ mb: 1, display: "flex", alignItems: "center" }} size={12}>
                  <Typography className={classes.HeaderTitle} component="div">
                    {`${Job_id ? "Edit Data Exchange Job" : "Add Data Exchange Job"}`}
                  </Typography>
                  <ButtonUI
                    variant="contained"
                    color="success"
                    disabled={
                      disableSaveButton() ||
                      exchangeJobState.loading ||
                      (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId))
                    }
                    onClick={handleSave}
                  >
                    Save
                  </ButtonUI>
                  <ButtonUI onClick={handleCancelClick} variant="contained" color="secondary" sx={{ ml: "15px" }}>
                    Cancel
                  </ButtonUI>

                  {Job_id ? (
                    <>
                      <ButtonUI
                        onClick={() => setOpenClone(true)}
                        variant="contained"
                        color="info"
                        sx={{ mx: "15px" }}
                        disabled={disableSaveButton()}
                      >
                        Clone
                      </ButtonUI>

                      <ButtonUI
                        variant="contained"
                        color="error"
                        onClick={() => {
                          setPrevState(setDeleteJobState, {
                            openConfirm: true,
                            confirmTitle: "Delete Data Exchange Job",
                            confirmMessage: `Are you sure you want to delete "${jobName}" Job?"`,
                          });
                        }}
                        disabled={!Job_id}
                      >
                        Delete
                      </ButtonUI>
                    </>
                  ) : (
                    <></>
                  )}

                  <Box sx={{ display: "inline-flex", verticalAlign: "sub", marginRight: "10px" }}>
                    <HelpContextIcon screen="Data Exchange Job" />
                  </Box>
                </Grid>

                <Grid className={classes.BigTileContainer} size={12}>
                  <FormTile>{addJobForm()}</FormTile>
                </Grid>
                <Grid className={classes.BigTileContainer} size={12}>
                  <SmallToolbar>Source</SmallToolbar>
                  <FormTile>
                    <SourceForm
                      setExchangeJobState={setExchangeJobState}
                      exchangeJobState={exchangeJobState}
                      participantsExcludeVisitor={participantsExcludeVisitor.current}
                      handleUserChange={handleUserChange}
                      handleRunUnderMyName={handleRunUnderMyName}
                      previousState={previousState.current}
                      accessRole={accessRole}
                      jobPurpose={jobPurpose}
                      setRunTimeState={setRunTimeState}
                      runTimeState={runTimeState}
                      loadSchemasClusters={loadSchemasClusters}
                    />
                  </FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Destination</SmallToolbar>
                  <FormTile>
                    <DestinationContext.Provider value={destContextValue}>
                      <DestinationForm
                        setExchangeJobState={setExchangeJobState}
                        exchangeJobState={exchangeJobState}
                        previousState={previousState.current}
                        participantsExcludeVisitor={participantsExcludeVisitor.current}
                        handleUserChange={handleUserChange}
                        handleRunUnderMyName={handleRunUnderMyName}
                        accessRole={accessRole}
                        jobPurpose={jobPurpose}
                        setRunTimeState={setRunTimeState}
                        runTimeState={runTimeState}
                        loadSchemasClusters={loadSchemasClusters}
                      />
                    </DestinationContext.Provider>
                  </FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Schedule</SmallToolbar>
                  <FormTile
                    style={{
                      overflowX: "auto",
                    }}
                  >
                    <Scheduler
                      scheduleEnabled={exchangeJobState.scheduleEnabled}
                      cronValue={values.cronValue}
                      dispatchValues={dispatchValues}
                      toggleValue={toggleSchedule}
                      timeZone={exchangeJobState.timeZone}
                      nextRunTime={exchangeJobState.nextRunTime}
                    />
                  </FormTile>
                </Grid>
                <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                  <SmallToolbar>Next Job</SmallToolbar>
                  <FormTile>
                    <NextJobForm
                      dataPipelineList={dataPipelineList}
                      nextJobToggle={nextJobState.nextJobToggle}
                      nextDataPipelineId={nextJobState.nextDataPipelineId}
                      nextJobId={nextJobState.nextJobId}
                      errorDataPipelineId={nextJobState.errorDataPipelineId}
                      errorJobId={nextJobState.errorJobId}
                      setNextJobState={setNextJobState}
                    />
                  </FormTile>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
      {notAllowedDialog.openConfirm && (
        <NotAllowedDialog
          notAllowedDialog={notAllowedDialog}
          onClose={() => {
            setNotAllowedDialog({
              openConfirm: false,
              confirmMessage: "",
              title: "",
            });
          }}
        />
      )}

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={exchangeJobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.dataExchange}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: deleteJobState.confirmTitle,
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: handleCancel,
            handleClick: deleteExchangeJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
      {exchangeJobState.mapping && (
        <MappingPopup
          destinationType={destinationType}
          exchangeJobState={exchangeJobState}
          setExchangeJobState={setExchangeJobState}
          delimeter={
            exchangeJobState.delimiter === "custom" ? exchangeJobState.customDelimeter : exchangeJobState.delimiter
          }
          nullValue={
            exchangeJobState.nullValue === "custom" ? exchangeJobState.customNullValue : exchangeJobState.nullValue
          }
          updatePreviousRef={updatePreviousRef}
          // If no change in previously selected options fields will get loaded again
          isFilePropertiesChanged={
            previousState.current.destination !== exchangeJobState.destination ||
            previousState.current.delimiter !== exchangeJobState.delimiter ||
            previousState.current.nullValue !== exchangeJobState.nullValue ||
            previousState.current.hasHeaderRow !== exchangeJobState.hasHeaderRow ||
            previousState.current.encoding !== exchangeJobState.encoding ||
            previousState.current.fieldEnclosedBy !== exchangeJobState.fieldEnclosedBy ||
            previousState.current.schemaName !== exchangeJobState.schemaName ||
            previousState.current.tableName !== exchangeJobState.tableName ||
            previousState.current.destinationAction !== exchangeJobState.destinationAction ||
            previousState.current.existingTableAction !== exchangeJobState.existingTableAction ||
            previousState.current.source !== exchangeJobState.source ||
            previousState.current.sourcePath !== exchangeJobState.sourcePath
          }
          existingFields={exchangeJobState.fields}
        />
      )}
    </CommonLayout>
  );
}
